import { formatCurrency } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type { UserCollege } from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Box, SxProps, Theme, Tooltip, Typography } from '@mui/material';

const STRINGS = appStrings.modules.myColleges;

export const EstimatedAmount = ({
  amount,
  tooltip = STRINGS.netCostEstimateTooltip,
}: Readonly<{ amount: string; tooltip?: string }>) => (
  <Box component="span" sx={estimatedSx}>
    {amount}
    <Tooltip title={tooltip} placement="top" arrow>
      <Typography component="span" variant="text-xs-regular" sx={markerSx}>
        {STRINGS.netCostEstimate}
      </Typography>
    </Tooltip>
  </Box>
);

const estimateTooltip = (
  source: UserCollege['net_cost_source'],
  hasFamilyIncome: boolean,
): string | null => {
  switch (source) {
    case 'award_letter':
    case null:
      return null;
    case 'scorecard':
      return STRINGS.netCostEstimateTooltip;
    case 'average':
      return hasFamilyIncome
        ? STRINGS.netCostAverageWithIncomeTooltip
        : STRINGS.netCostAverageTooltip;
  }
};

export const NetCostText = ({
  college,
  hasFamilyIncome,
}: Readonly<{ college: UserCollege; hasFamilyIncome: boolean }>) => {
  if (college.net_cost === null) return null;

  const amount = formatCurrency(college.net_cost);
  const tooltip = estimateTooltip(college.net_cost_source, hasFamilyIncome);

  return (
    <Typography variant="text-sm-medium" sx={amountSx}>
      {tooltip ? <EstimatedAmount amount={amount} tooltip={tooltip} /> : amount}
    </Typography>
  );
};

const amountSx: SxProps<Theme> = ({ backrs }) => ({
  whiteSpace: 'nowrap',
  color: backrs.colors['Colors/Text/text-primary'],
});

const estimatedSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: backrs.spacing['spacing-xs'],
  whiteSpace: 'nowrap',
});

const markerSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
  cursor: 'help',
});
